import type {
  QuestionnaireItem,
  QuestionnaireItemEnableWhen,
  QuestionnaireResponse,
  QuestionnaireResponseItemAnswer,
} from '@medplum/fhirtypes';

const firstAnswer = (
  response: QuestionnaireResponse | undefined | null,
  linkId: string,
): QuestionnaireResponseItemAnswer | undefined => response?.item?.find((i) => i.linkId === linkId)?.answer?.[0];

const answerMatchesEnableWhen = (
  answer: QuestionnaireResponseItemAnswer | undefined,
  condition: QuestionnaireItemEnableWhen,
): boolean => {
  if (!answer) return false;
  if (condition.answerCoding) {
    return answer.valueCoding?.code === condition.answerCoding.code;
  }
  if (condition.answerString !== undefined) {
    return answer.valueString === condition.answerString;
  }
  if (condition.answerBoolean !== undefined) {
    return answer.valueBoolean === condition.answerBoolean;
  }
  return false;
};

/**
 * Whether a Questionnaire item is currently enabled given `response`, per FHIR `enableWhen` /
 * `enableBehavior`. Items without `enableWhen` are always enabled. Only the `=` operator is
 * supported today (enough for onboarding-questions “Other / please specify” follow-ups).
 */
export const isQuestionnaireItemEnabled = (
  item: QuestionnaireItem,
  response: QuestionnaireResponse | undefined | null,
): boolean => {
  const conditions = item.enableWhen;
  if (!conditions || conditions.length === 0) return true;

  const results = conditions.map((condition) => {
    if (condition.operator !== '=') return false;
    return answerMatchesEnableWhen(firstAnswer(response, condition.question), condition);
  });

  return item.enableBehavior === 'any' ? results.some(Boolean) : results.every(Boolean);
};
